import React from "react";
import {useMutation} from '@apollo/client';
import withHooks, {useState} from "react-with-hooks";
import {loadStripe} from "@stripe/stripe-js";
import {Elements, PaymentElement, useElements, useStripe} from "@stripe/react-stripe-js";

import WithUserForm from "../../Register/WithUserForm";
import Step from "../Step";
import styles from "../styles.module.css";
import stylesButton from "../../utils/css/button.module.css";
import {CREATE_SUBSCRIPTION} from "../../Register/WithUserForm/queries";

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CheckoutForm = ({ selectedMembership, onSuccess, onError, userEmail }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const [createSubscription] = useMutation(CREATE_SUBSCRIPTION);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    setIsLoading(true);
    // Submit the Stripe form first
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setMessage(submitError.message);
      onError && onError(submitError);
      setIsLoading(false);
      return;
    }
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      elements,
      params: {
        billing_details: {
          email: userEmail,
        },
      },
    });
    if (error) {
      setMessage(error.message);
      onError && onError(error);
      setIsLoading(false);
      return;
    }
    // Send payment method to backend to create subscription
    try {
      const { data } = await createSubscription({
        variables: {
          paymentMethodId: paymentMethod.id,
          priceId: selectedMembership.priceId
        }
      });
      if (data.createSubscription) {
        onSuccess && onSuccess(data.createSubscription);
      }
    } catch (backendError) {
      setMessage("Payment failed. Please check your card details and try again.");
      onError && onError(backendError);
    }
    setIsLoading(false);
  };
  return (
    <form onSubmit={handleSubmit} className={styles.checkout_form}>
      <div className={styles.payment_fields}>
        <PaymentElement options={{
          fields: {
            billingDetails: {
              name: 'auto',
              address: {
                country: 'auto'
              }
            }
          }
        }} />
      </div>
      {message && <div className={styles.error_message}>{message}</div>}
      <button
        disabled={isLoading || !stripe || !elements}
        className={`${stylesButton.button} ${stylesButton.button_submit} ${styles.pay_button}`}
        type="submit"
      >
        {isLoading ? "Processing..." : "Pay"}
      </button>
    </form>
  );
};

const StepCheckoutMembership = withHooks((props) => {
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [createSubscription, { loading, error }] = useMutation(CREATE_SUBSCRIPTION);

  const membershipOptions = [
    {
      id: "icon_app",
      name: "App membership",
      userRoleName: "Icon",
      fee: "£2,200/year",
      features: ["Create your own luxury shop", "Access our online community of brands and jobs"],
      priceId: process.env.REACT_APP_ICON_APP_MEMBERSHIP_STRIPE_PRICE_ID
    },
    {
      id: "icon_studio",
      name: "Studio membership",
      userRoleName: "Icon",
      fee: "£3,300/year",
      features: ["Full app access", "Access to Schmodel.studio clubs", "Exclusive member deals"],
      priceId: process.env.REACT_APP_ICON_STUDIO_MEMBERSHIP_STRIPE_PRICE_ID
    },
    {
      id: "brand_app",
      name: "App membership",
      userRoleName: "Brand",
      fee: "£1,350/year",
      features: ["TODO", "TODO", "TODO"],
      priceId: process.env.REACT_APP_BRAND_APP_MEMBERSHIP_STRIPE_PRICE_ID
    },
    {
      id: "brand_studio",
      name: "Studio membership",
      userRoleName: "Brand",
      fee: "£12,000/year",
      features: ["TODO", "TODO", "TODO"],
      priceId: process.env.REACT_APP_BRAND_STUDIO_MEMBERSHIP_STRIPE_PRICE_ID
    }
  ];

  const handleMembershipSelect = (membership) => {
    if (selectedMembership?.id === membership.id) {
      setSelectedMembership(null);
    } else {
      setSelectedMembership(membership);
    }
  };

  const handleCheckout = async () => {
    if (!selectedMembership || !createSubscription) return;
    setShowCheckout(true);
  };

  const handleBack = () => {
    if (showCheckout) {
      setShowCheckout(false);
    } else {
      props.onBack();
    }
  };

  const elementsOptions = {
    mode: 'setup',
    currency: 'gbp',
    setupFutureUsage: 'off_session',
    paymentMethodCreation: 'manual',
    paymentMethodTypes: ['card'],
    appearance: {
      theme: 'night',
      variables: {
        colorPrimary: '#0570de',
        colorBackground: '#1a1a1a',
        colorText: '#ffffff',
        colorDanger: '#df1b41',
        fontFamily: 'Ideal Sans, system-ui, sans-serif',
        spacingUnit: '4px',
        borderRadius: '8px',
        colorTextSecondary: '#b3b3b3',
        colorTextPlaceholder: '#666666',
        colorIconTab: '#ffffff',
        colorLogo: '#ffffff'
      },
      rules: {
        '.Tab': {
          backgroundColor: '#2a2a2a',
          border: '1px solid #404040',
          color: '#ffffff'
        },
        '.Tab:hover': {
          backgroundColor: '#333333'
        },
        '.Tab--selected': {
          backgroundColor: '#0570de',
          border: '1px solid #0570de'
        },
        '.Input': {
          backgroundColor: '#2a2a2a',
          border: '1px solid #404040',
          color: '#ffffff'
        },
        '.Input:focus': {
          border: '1px solid #0570de',
          boxShadow: '0 0 0 1px #0570de'
        }
      },
      labels: 'above'
    }
  };

  if (showCheckout) {
    return (
      <Step backgroundImage={""} classNames={`${styles.step_membership} ${styles.checkout_membership_step}`}>
        <p className={styles.sub_title}>Complete your payment below.</p>
        
        <div className={styles.membership_checkout_container}>
          <div id="checkout" className={styles.embedded_checkout}>
            <div className={styles.selected_checkout_membership}>
              <h3>{selectedMembership.userRoleName} - {selectedMembership.name}</h3>
              <p>{selectedMembership.fee}</p>
            </div>
            
            {selectedMembership && (
              <Elements
                stripe={stripePromise}
                options={elementsOptions}
              >
                <CheckoutForm 
                  selectedMembership={selectedMembership}
                  onSuccess={(data) => {
                    console.log("Subscription created successfully!", data);
                    if (props.machine?.service) {
                      props.machine.service.send({ type: "NEXT" });
                    }
                  }}
                  onError={(error) => {
                    console.error("Payment error:", error);
                  }}
                  userEmail={props.initialValues?.email}
                />
              </Elements>
            )}
          </div>
        </div>
        
        <div className={styles.membership_action_container}>
          <button
            className={`${stylesButton.button} ${stylesButton.button_back}`}
            onClick={handleBack}
          >
            Back to Membership selection
          </button>
        </div>

        {error && (
          <div className={styles.error_message}>
            Error: {error.message}
          </div>
        )}
      </Step>
    );
  }

  return (
      <Step backgroundImage={""} classNames={`${styles.step_membership} ${styles.checkout_membership_step}`}>
        <p className={styles.sub_title}>You've done it!<br/>Thanks for applying.</p>

        <div className={styles.membership_grid}>
          <h3 className={styles.membership_row_header}>I'm a Fashion Icon</h3>
          <div className={styles.membership_row}>
            {membershipOptions.slice(0, 2).map((membership) => (
                <div
                    key={membership.id}
                    className={`${styles.membership_card} ${selectedMembership?.id === membership.id ? styles.selected_membership_card : ""}`}
                    onClick={() => handleMembershipSelect(membership)}
                >
                  <h2 className={styles.membership_name}>{membership.name}</h2>
                  <p className={styles.membership_fee}>{membership.fee}</p>
                  <ul className={styles.membership_features}>
                    {membership.features.map((feature, index) => (
                      <li key={index}>• {feature}</li>
                    ))}
                  </ul>
                </div>
            ))}
          </div>
          
          <h3 className={styles.membership_row_header}>I'm a Brand</h3>
          <div className={styles.membership_row}>
            {membershipOptions.slice(2, 4).map((membership) => (
                <div
                    key={membership.id}
                    className={`${styles.membership_card} ${selectedMembership?.id === membership.id ? styles.selected_membership_card : ""}`}
                    onClick={() => handleMembershipSelect(membership)}
                >
                  <h2 className={styles.membership_name}>{membership.name}</h2>
                  <p className={styles.membership_fee}>{membership.fee}</p>
                  <ul className={styles.membership_features}>
                    {membership.features.map((feature, index) => (
                      <li key={index}>• {feature}</li>
                    ))}
                  </ul>
                </div>
            ))}
          </div>
        </div>

        <div className={styles.membership_action_container}>
          <button
              disabled={!selectedMembership || !createSubscription}
              className={`${stylesButton.button} ${stylesButton.button_submit} ${styles.button_submit}`}
              onClick={handleCheckout}
          >
            {loading ? "Processing..." : "Continue to Checkout"}
          </button>
          <button
              className={`${stylesButton.button} ${stylesButton.button_back}`}
              onClick={handleBack}
          >
            Back
          </button>
        </div>

        {error && (
            <div className={styles.error_message}>
              Error: {error.message}
            </div>
        )}
      </Step>
  );
});

class StepCheckoutMembershipContainer extends React.Component {
  onSubmit = async (values, _, props) => {
    // This is a placeholder for any form submission logic
    // For now, we're just transitioning to the next step
    return props.machine.service.send({
      type: "NEXT",
    });
  };

  render() {
    return (
      <WithUserForm onSubmit={this.onSubmit}>
        {(props) => (
          <StepCheckoutMembership 
            {...props} 
            onBack={() => props.machine.service.send({ type: "BACK" })}
            userId={props.initialValues?.id}
            userEmail={props.initialValues?.email}
          />
        )}
      </WithUserForm>
    );
  }
}

export default StepCheckoutMembershipContainer; 